import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { withdrawalsRepositoryToken } from './repository';
import type { WithdrawalDetail } from './types';

export function WithdrawalRetryDialog({
  dataSource,
  onClose,
  open,
  withdrawal,
}: {
  readonly dataSource: DataSource;
  readonly onClose: (completed: boolean) => void;
  readonly open: boolean;
  readonly withdrawal: WithdrawalDetail;
}) {
  const repository = useRepository(withdrawalsRepositoryToken);
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const valid = reason.trim().length > 0 && reason.trim().length <= 2000;

  const updateReason = (value: string) => {
    setReason(value);
    setConfirmed(false);
    setScheduled(false);
    setError(null);
    idempotencyKeyRef.current = crypto.randomUUID();
  };
  const reset = () => {
    setReason('');
    setConfirmed(false);
    setBusy(false);
    setScheduled(false);
    setError(null);
    submittingRef.current = false;
    idempotencyKeyRef.current = crypto.randomUUID();
  };
  const close = () => {
    if (busy) return;
    const completed = scheduled;
    reset();
    onClose(completed);
  };
  const submit = async () => {
    if (!valid || !confirmed || busy || submittingRef.current || scheduled) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.retry(withdrawal.id, { reason: reason.trim() }, idempotencyKeyRef.current);
      setScheduled(true);
      idempotencyKeyRef.current = crypto.randomUUID();
    } catch (cause) {
      setError(safeError(cause, t('withdrawals.retry.errors.submit')));
      submittingRef.current = false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      aria-describedby="withdrawal-retry-description"
      fullWidth
      maxWidth="sm"
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('withdrawals.retry.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source={dataSource} />
          <DialogContentText id="withdrawal-retry-description">
            {t('withdrawals.retry.description')}
          </DialogContentText>
          <Stack spacing={0.5}>
            <Typography color="text.secondary" variant="caption">
              {t('withdrawals.retry.transferId')}
            </Typography>
            <Typography component="code">{withdrawal.transferId}</Typography>
          </Stack>
          <Typography>
            {t('withdrawals.retry.stateChange', { status: withdrawal.status })}
          </Typography>
          <Alert severity="warning">{t('withdrawals.retry.impact')}</Alert>
          {scheduled ? (
            <Alert severity="success">{t('withdrawals.retry.scheduled')}</Alert>
          ) : (
            <>
              <TextField
                autoFocus
                disabled={busy}
                label={t('withdrawals.retry.reason')}
                minRows={2}
                multiline
                onChange={(event) => updateReason(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 2000, required: true } }}
                value={reason}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmed}
                    disabled={busy}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                }
                label={t('withdrawals.retry.confirm')}
              />
            </>
          )}
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.close')}
        </Button>
        {scheduled ? null : (
          <Button
            disabled={!valid || !confirmed || busy}
            loading={busy}
            onClick={() => void submit()}
            variant="contained"
          >
            {t('withdrawals.retry.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function safeError(cause: unknown, fallback: string): AppError {
  const error = isAppError(cause) ? cause : null;
  return {
    status: error?.status ?? null,
    code: error?.code ?? 'WITHDRAWAL_RETRY_FAILED',
    message: fallback,
    requestId: error?.requestId ?? null,
    fieldErrors: {},
    retryAfterSeconds: error?.retryAfterSeconds ?? null,
  };
}

function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AppError>).code === 'string' &&
    'requestId' in value
  );
}
